import React, { Component } from 'react';
import styled from 'styled-components';
import { makeExternalUrl, attachLocale, attachCategorySlug, attachSubcategorySlug } from '../../../../lib/utils';
import { DATA_TRACK_TYPE_INTERNAL } from '../../../../lib/constants';
import ShopsGrid from '../shared/ShopsGrid';
import { getBrowseCategoriesAnalytics } from './browseCategoriesAnalytics';
import {
  getAllCategoryAnalyticsSlug,
  getBrowseCategoriesLevelThreeAnalytics,
} from './levelThreeBrowseCategoriesAnalytics';

const CategoryIconImg = styled.img`
  width: ${p => p.theme.unit(3)};
  padding: 0 ${p => p.theme.unit(3)};
`;

const CategoryTitleSpan = styled.span`
  ${p => p.theme.typographyRebrand.body.md};
  padding-top: 2px; // to center text
  text-decoration: none;
`;

const SubCategoryTitleSpan = styled.span`
  ${p => p.theme.typographyRebrand.body.md};
  color: ${p => p.theme.c.text.interactive};
  text-decoration: none;
  padding: 0 ${p => p.theme.unit(4)};
  :hover {
    text-decoration: ${p => (p.underline ? 'underline' : 'none')};
    color: ${p => p.theme.c.text['interactive-hover']};
  }
`;

const CategoryAnchor = styled.a`
  display: inline-flex;
  align-items: center;
  height: ${p => p.theme.unit(5)};
  width: 100%;
  text-decoration: none;
  cursor: pointer;
  color: ${p => p.theme.c.text.interactive};
  :hover {
    text-decoration: underline;
    color: ${p => p.theme.c.text['interactive-hover']};
  }
`;

const SubCategoryAnchor = styled(CategoryAnchor)`
`;

const CategoryListDiv = styled.div`
  width: ${p => p.theme.unit(40)};
  background-color: ${p => p.theme.c.bg.primary};
  min-height: ${p => p.theme.unit(50)};
  padding-top: ${p => p.theme.unit(2)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  z-index: ${p => p.theme.elevation[2]};
`;

const CategorySideBarDiv = styled.div`
  width: ${p => p.theme.unit(51)};
  min-height: ${p => p.theme.unit(40)};
  background-color: ${p => p.theme.c.bg.primary};
  padding-top: ${p => p.theme.unit(3)};
  z-index: ${p => p.theme.elevation[2]};
  box-shadow: inset 8px 0 16px -12px ${({ theme }) => theme.c.border['box-shadow']},
              4px -2px 8px -8px ${({ theme }) => theme.c.border['box-shadow']},
              2px 2px 4px -1px ${({ theme }) => theme.c.border['box-shadow']};
  border: solid 1px ${p => p.theme.c.border.primary};
  border-left: none;
  ${p => p.sideBarHeight && `height: ${Math.max(p.sideBarHeight - p.theme.unitInt(3), 0)}px;`}
`;

const CategoriesDropdownDiv = styled.div`
  display: flex;
  flex-direction: row;
  box-shadow: 0 2px 8px 0 ${({ theme }) => theme.c.border['box-shadow']};
  border: solid 1px ${p => p.theme.c.border.primary};
  margin: ${p => p.theme.unit(2)};
  margin-top: ${p => p.theme.unit(1)};
  margin-left: 0;
`;

const TopSectionUl = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const DropdownList = styled.li`
  display: flex;
  align-items: center;
  :hover {
    text-decoration: underline;
    background-color: ${p => p.theme.c.bg.secondary};
  }
`;

const BottomSectionDiv = styled.div`
  padding: ${p => p.theme.unit(3)} 0;
  border-top: 1px solid ${p => p.theme.c.border.primary};
`;

const RightArrowButton = styled.button`
  margin-left: auto;
  margin-right: ${p => p.theme.unit(1)};
  background: transparent;
  border: none;
  cursor: pointer;

  color: ${p => p.theme.c.icon.primary};
`;

const PositionAbsoluteSideBar = styled.div`
  position: absolute;
  top: ${p => p.theme.unit((p.top || p.top === 0) ? p.top : 'auto')};
  right: ${p => p.theme.unit((p.right || p.right === 0) ? p.right : 'auto')};
  bottom: ${p => p.theme.unit((p.bottom || p.bottom === 0) ? p.bottom : 'auto')};
  left: ${p => p.theme.unit((p.left || p.left === 0) ? p.left : 'auto')};
  z-index: ${p => p.theme.elevation[2]};
  margin-top: ${p => p.theme.unit(1)};
  ${p => (p.showMenu ? '' : 'display: none;')};
`;

const RightArrow = () => (
  <span>
    <svg
      width="24px"
      height="24px"
      viewBox="0 0 24 24"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false">
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
          <path
           fill='currentColor'
           d="M14.2,18.67375 L8,12.42275 L14.203,6.21975 C14.496,5.92675 14.97,5.92675 15.263,6.21975 C15.556,6.51275 15.556,6.98775 15.263,7.28075 L10.117,12.42675 L15.266,17.61775 C15.557,17.91175 15.555,18.38675 15.261,18.67775 C15.115,18.82375 14.924,18.89575 14.733,18.89575 C14.54,18.89575 14.347,18.82175 14.2,18.67375 Z"  transform="translate(11.741441, 12.447875) rotate(-180.000000) translate(-11.741441, -12.447875) " />
      </g>
    </svg>
  </span>
);

const CategorySideBar = (
  {
    subCategories = [],
    shops = [],
    locale,
    parentCategorySlug,
    parentCategoryAnalyticsSlug,
    parentCategoryName,
    pageStateModifiers,
    globalPageConfig = {},
    sideBarHeight,
  }) => {
  if (!subCategories || !subCategories.length) {
    return null;
  }
  const { categoryDirectoryParentCategoryPrefix = 'All' } = globalPageConfig;

  let resolvedParentCategorySlug = attachCategorySlug(parentCategorySlug);
  resolvedParentCategorySlug = attachLocale(resolvedParentCategorySlug, locale);

  const parentTrack = getBrowseCategoriesLevelThreeAnalytics({
    analyticsSlug: getAllCategoryAnalyticsSlug(parentCategoryAnalyticsSlug),
    parentCategoryAnalyticsSlug,
    url: resolvedParentCategorySlug,
  });
  const {
    dataTrackingId: parentDataTrackingId,
    dataTrack: parentDataTrack,
    url: parentUrl,
  } = parentTrack;
  const parentCategoryComponent = (
    <SubCategoryAnchor
      href={parentUrl}
      id={parentDataTrackingId}
      data-track-id={parentDataTrackingId}
      data-track-click={parentDataTrack}
      data-track-type={DATA_TRACK_TYPE_INTERNAL}>
      <SubCategoryTitleSpan underline>
        {`${categoryDirectoryParentCategoryPrefix} ${parentCategoryName}`}
      </SubCategoryTitleSpan>
    </SubCategoryAnchor>
  );

  const subCategoriesList = subCategories.map((cat) => {
    const {
      slug = '',
      analyticsSlug = '',
      name = '',
    } = cat;

    let resolvedSubCategorySlug = attachSubcategorySlug(parentCategorySlug, slug);
    resolvedSubCategorySlug = attachLocale(resolvedSubCategorySlug, locale);

    const track = getBrowseCategoriesLevelThreeAnalytics({
      analyticsSlug,
      parentCategoryAnalyticsSlug,
      url: resolvedSubCategorySlug,
    });

    const {
      dataTrackingId,
      dataTrack,
      url,
    } = track;


    return (
      // check if the name start with space so that we don't display placeholder subcategories and show empty link
      !cat.name.startsWith(' ') ? (<SubCategoryAnchor
        key={name}
        href={url}
        id={dataTrackingId}
        data-track-id={dataTrackingId}
        data-track-click={dataTrack}
        data-track-type={DATA_TRACK_TYPE_INTERNAL}
      >
        <SubCategoryTitleSpan underline>{cat.name}</SubCategoryTitleSpan>
      </SubCategoryAnchor>) : null);
  });

  const shopsComponent = shops && shops.length ?
    <ShopsGrid
      locale={locale}
      parentCategoryAnalyticsSlug={parentCategoryAnalyticsSlug}
      shops={shops} /> :
    null;

  return (
    <CategorySideBarDiv sideBarHeight={sideBarHeight}>
      {parentCategoryComponent}
      {subCategoriesList}
      {shopsComponent}
    </CategorySideBarDiv>
  );
};

const BottomSectionLinks = (props) => {
  const {
    categoriesDropdownLinks,
    locale,
  } = props;
  if (!categoriesDropdownLinks || !categoriesDropdownLinks.length) {
    return null;
  }

  const linksComponents = categoriesDropdownLinks.map((link, index) => {
    const resolvedLink = attachLocale(link.url, locale);
    const track = getBrowseCategoriesAnalytics({
      analyticsSlug: link.analyticsSlug,
      url: resolvedLink,
    });

    return (
      <CategoryAnchor
        onKeyPress={(e) => { e.stopPropagation(); }}
        key={`${link.text}-${index}`}
        href={track.url}
        id={track.dataTrackingId}
        data-track-id={track.dataTrackingId}
        data-track-click={track.dataTrack}
        data-track-type={DATA_TRACK_TYPE_INTERNAL}>
        <SubCategoryTitleSpan>{link.text}</SubCategoryTitleSpan>
      </CategoryAnchor>
    );
  });

  return (
    <BottomSectionDiv>
      {linksComponents}
    </BottomSectionDiv>
  );
};


class CategorySlot extends Component {
  constructor(props) {
    super(props);
    this.state = {
      displaySidebar: false,
      subCategories: [],
      shops: [],
      parentCategorySlug: '',
      parentCategoryName: '',
      parentCategoryTags: {},
      sideBarHeight: null,
    };
    this.categoryMouseEnter = this.categoryMouseEnter.bind(this);
    this.sideBarMenuHandler = this.sideBarMenuHandler.bind(this);
  }

  componentDidMount() {
    this.setState({ sideBarHeight: document.getElementById('CategoriesDropdownDivId').clientHeight });
  }

  categoryMouseEnter(subCategories, shops, parentCategorySlug, parentCategoryName, parentCategoryTags) {
    this.setState(
      {
        displaySidebar: true,
        subCategories,
        shops,
        parentCategorySlug,
        parentCategoryName,
        parentCategoryTags,
      });
  }

  sideBarMenuHandler() {
    this.setState({ displaySidebar: !this.state.displaySidebar });
  }

  render() {
    const {
      name,
      slug,
      analyticsSlug,
      isInCategoryNav, // TODO: hide category from dropdown when isInCategoryNav is false
      categoryIconUrl,
      subCategories,
      locale,
      mouseEnter,
      shops,
      tags,
      featureShops = [],
      pageStateModifiers,
      globalPageConfig,
      sidebarToggleOpen,
      sidebarToggleClose,
    } = this.props;

    const resolvedIconUrl = makeExternalUrl(categoryIconUrl);
    let resolvedCategorySlug = attachCategorySlug(slug);
    resolvedCategorySlug = attachLocale(resolvedCategorySlug, locale);

    const shopsToFeature = featureShops && featureShops.length ? featureShops : shops;

    const track = getBrowseCategoriesAnalytics({
      analyticsSlug,
      url: resolvedCategorySlug,
    });
    const {
      dataTrackingId,
      dataTrack,
      url,
    } = track;

    const sidebarComponent =
      (<CategorySideBar
        locale={locale}
        subCategories={subCategories}
        shops={shopsToFeature}
        parentCategorySlug={slug}
        parentCategoryAnalyticsSlug={analyticsSlug}
        parentCategoryName={name}
        pageStateModifiers={pageStateModifiers}
        globalPageConfig={globalPageConfig}
        sideBarHeight={this.state.sideBarHeight} />);

    const slotComponent = (
      <DropdownList
        role="none"
        onMouseEnter={() => { this.categoryMouseEnter(subCategories, shopsToFeature, slug, name, tags); }}
        onMouseLeave={() => { this.setState({ displaySidebar: false }); }}>
        <CategoryAnchor
          role="menuitem"
          href={url}
          id={dataTrackingId}
          data-track-id={dataTrackingId}
          data-track-click={dataTrack}
          data-track-type={DATA_TRACK_TYPE_INTERNAL}>
          <CategoryIconImg src={resolvedIconUrl} alt="" />
          <CategoryTitleSpan>{name}</CategoryTitleSpan>
        </CategoryAnchor>
        <RightArrowButton
          aria-haspopup="true"
          aria-expanded={this.state.displaySidebar}
          aria-label={this.state.displaySidebar ? sidebarToggleClose : sidebarToggleOpen}
          title={this.state.displaySidebar ? sidebarToggleClose : sidebarToggleOpen}
          onClick={this.sideBarMenuHandler}
          >
          <RightArrow />
        </RightArrowButton>
        <PositionAbsoluteSideBar
          top={0}
          left={40}
          showMenu={this.state.displaySidebar}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              this.setState({ displaySidebar: false });
            }
          }}>{sidebarComponent}</PositionAbsoluteSideBar>
      </DropdownList>
    );
    return (slotComponent);
  }
}

class CategoriesDropdown extends Component {
  constructor(props) {
    super(props);
    this.state = {
      displaySidebar: true,
      subCategories: [],
      shops: [],
      parentCategorySlug: '',
      parentCategoryName: '',
      parentCategoryTags: {},
    };
  }

  render() {
    const {
      rootCategory,
      locale,
      categoriesDropdownLinks,
      pageStateModifiers,
      globalPageConfig,
    } = this.props;

    const categories = rootCategory && rootCategory.subCategories &&
      rootCategory.subCategories.length >= 0 ? rootCategory.subCategories : [];
    const categoriesList = categories && (categories.length >= 0) &&
      categories.map((cat, index) => {
        return (<CategorySlot
          key={`${cat.name}-${index}`}
          locale={locale}
          mouseEnter={this.categoryMouseEnter}
          pageStateModifiers={pageStateModifiers}
          globalPageConfig={globalPageConfig}
          {...cat} />);
      });


    return (
      <CategoriesDropdownDiv id="CategoriesDropdownDivId">
        <CategoryListDiv>
          <TopSectionUl role="menu">
            {categoriesList}
          </TopSectionUl>
          <BottomSectionLinks
            categoriesDropdownLinks={categoriesDropdownLinks}
            locale={locale} />
        </CategoryListDiv>
      </CategoriesDropdownDiv>
    );
  }
}

export default CategoriesDropdown;
